import cx from 'classnames';
import { Anchor, AsyncImage, IAnchorProps } from 'components/ui';
import { useWhiteLabelContext } from 'components/white-label';
import { AnimatePresence, motion } from 'framer-motion';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { Organization } from 'lib/types/company';
import _ from 'lodash';
import useTranslation from 'next-translate/useTranslation';
import { ReactNode } from 'react';
import css from './AuthLayout.module.scss';

const motionProps = {
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
  transition: { duration: 0.1 },
};

interface IAuthLayoutProps {
  animationKey?: string;
  children: ReactNode;
  variant?: 'admin' | 'white-label';
}

export const AuthLayout = ({
  animationKey,
  children,
  variant,
}: IAuthLayoutProps) => (
  <div className={css.layout}>
    <div className={cx(css.wrapper)}>
      <Logo />
      <main>
        <div aria-live="polite">
          <AnimatePresence mode="wait">
            <motion.div key={animationKey} {...motionProps}>
              {children}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      <Links variant={variant} />
    </div>
  </div>
);

const Logo = () => {
  const { organization, organizationTheme, organizationName } =
    useWhiteLabelContext();

  return (
    <AsyncImage
      data-theme-id="logoUrl"
      className={css.logo}
      height={organization === Organization.LIFE_LABS ? 36 : 24}
      src={organizationTheme.logoUrl}
      alt={organizationName}
    />
  );
};

interface ILinksProps {
  variant?: 'admin' | 'white-label';
}

const Links = ({ variant = 'white-label' }: ILinksProps) => {
  const { legalAndPrivacyUrl, primaryColor, supportEmail } =
    useWhiteLabelContext();
  const { t } = useTranslation('shared/auth');

  const getAnchorProps = (): Partial<IAnchorProps> => {
    switch (variant) {
      case 'admin':
        return { color: 'blue' };
      case 'white-label':
      default:
        return { style: { color: primaryColor } };
    }
  };

  return (
    <div className={css.linksContainer}>
      <Anchor
        href={`${getEnv(EnvVariable.API_PATHNAME)}/login`}
        {...getAnchorProps()}
      >
        {t('admin-login')}
      </Anchor>
      <Dot />
      <Anchor
        {...getAnchorProps()}
        href={legalAndPrivacyUrl}
        target={_.startsWith(legalAndPrivacyUrl, 'http') ? '_blank' : undefined}
      >
        {t('legal-and-privacy')}
      </Anchor>
      <Dot />
      <Anchor href={`mailto:${supportEmail}`} {...getAnchorProps()}>
        {t('contact-us')}
      </Anchor>
    </div>
  );
};

const Dot = () => (
  <div aria-hidden className={css.dot}>
    ·
  </div>
);
